import { useCallback, useEffect, useRef } from 'react';
import { API_URL } from '../constants';
import { useUserInfoContext } from '../contexts/UserInfoContext';

export interface ForwardedTavusEvent {
  receivedAtMs: number;
  data: any;
}

const FLUSH_MS = 2000;
const MAX_BATCH = 200;
const ENABLED = process.env.REACT_APP_VOICE_TRACE !== 'false';

/** Speaking, utterance and interruption events: what the replica spoke and when the learner cut in. */
export function isTraceableTavusEvent(data: any): boolean {
  const type = String(data?.event_type ?? data?.type ?? '');
  return type.startsWith('conversation.') && /utterance|speaking|interrupt/.test(type);
}

/**
 * Adds an event to the pending batch. Streaming utterance updates repeat the accumulated
 * text, so only the latest one per inference and role is kept: the last state is what was spoken.
 */
export function addTavusEvent(batch: ForwardedTavusEvent[], event: ForwardedTavusEvent): ForwardedTavusEvent[] {
  const type = String(event.data?.event_type ?? '');
  const key = (e: ForwardedTavusEvent) =>
    `${e.data?.inference_id ?? e.data?.properties?.inference_id ?? ''}|${e.data?.properties?.role ?? ''}`;
  const kept = type.endsWith('.streaming')
    ? batch.filter((e) => !(String(e.data?.event_type ?? '') === type && key(e) === key(event)))
    : batch;
  return [...kept, event].slice(-MAX_BATCH);
}

/**
 * Forwards Tavus CVI events to the backend for voice-turn tracing, independent of the
 * debug Events panel. Off with REACT_APP_VOICE_TRACE=false.
 */
export function useTavusEventForwarding(conversationId: string | undefined) {
  const { userInfo } = useUserInfoContext();
  const tokenRef = useRef(userInfo?.accessToken);
  tokenRef.current = userInfo?.accessToken;
  const batchRef = useRef<ForwardedTavusEvent[]>([]);

  const flush = useCallback(() => {
    const events = batchRef.current;
    if (!conversationId || events.length === 0) return;
    batchRef.current = [];
    fetch(`${API_URL}/conversation/${encodeURIComponent(conversationId)}/events`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}),
      },
      body: JSON.stringify({ events }),
    }).catch(() => undefined);
  }, [conversationId]);

  useEffect(() => {
    if (!ENABLED || !conversationId) return;
    const timer = setInterval(flush, FLUSH_MS);
    return () => {
      clearInterval(timer);
      flush();
    };
  }, [conversationId, flush]);

  return useCallback((data: any) => {
    if (!ENABLED || !isTraceableTavusEvent(data)) return;
    batchRef.current = addTavusEvent(batchRef.current, { receivedAtMs: Date.now(), data });
  }, []);
}
